/**
 * FP3-044 Phase A — Admin Team tab.
 * FP3-055 — Shared assistant providers inside organizations.
 *
 * Lists every provider on the student's team with their role, primary
 * flag, direct/consult/eval-only flags, supervising provider link,
 * and assigned domain chips.
 *
 * Admin actions:
 *   - Add a new provider (picker filtered to providers in same org)
 *   - Remove an active provider (preserves history — soft remove via
 *     PATCH to drop pid from assigned_providers + team_assignments)
 *   - Change primary provider (exactly one)
 *   - Toggle direct service / evaluation team
 *   - Mark / unmark a row as assistant_assignment WITH required
 *     same-org supervising_provider_id (FP3-055 — picker prioritises
 *     compatible disciplines: SLPA→SLP, COTA→OT, PTA→PT)
 *   - Change supervising provider inline on an existing assistant row
 *   - Edit assigned_service_domains
 *
 * Drill-in: each row has "View as <Provider>" → /students/{id}?as_provider=<pid>
 * which opens the existing read-only provider workspace shadow.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Crown, UserMinus, UserPlus, Eye, ShieldCheck, X as XIcon,
  PencilSimple,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";

// FP3-055 — Detect whether a provider is an "assistant-flavoured"
// role from the discipline string the backend stamps on the
// providers collection (e.g. "Speech-Language Pathology Assistant",
// "Certified Occupational Therapy Assistant",
// "Physical Therapist Assistant"). Used to (a) hide them from the
// supervisor picker — assistants cannot supervise assistants — and
// (b) drive the SLPA→SLP / COTA→OT / PTA→PT compatibility sort.
function isAssistantRole(role) {
  const r = (role || "").toString();
  return /assistant|aide/i.test(r);
}

// Returns a normalized discipline tag we can use to rank a
// supervisor as "compatible" with the assistant being placed.
function disciplineTag(role) {
  const r = (role || "").toString().toLowerCase();
  if (r.includes("speech") || r.includes("slp")) return "speech";
  if (r.includes("occupational") || r.includes("ot ") || /\bot\b/.test(r) || r.includes("cota")) return "ot";
  if (r.includes("physical") || /\bpt\b/.test(r) || r.includes("pta")) return "pt";
  if (r.includes("psych")) return "psych";
  if (r.includes("special ed") || r.includes("teacher")) return "ed";
  return "other";
}

export default function AdminTeamTab({
  studentId, student, team = [], onChanged,
  readOnly = false, compactPreview = false,
}) {
  const [allProviders, setAllProviders] = useState([]);
  const [adding, setAdding] = useState(false);
  // FP3-055 — When a row is being marked assistant (or its
  // supervisor is being changed), we open an inline picker for
  // that specific row id.
  const [supervisorPickerFor, setSupervisorPickerFor] = useState("");
  const [savingFor, setSavingFor] = useState("");

  useEffect(() => {
    api.getProviders()
      .then((r) => setAllProviders(Array.isArray(r.data?.providers) ? r.data.providers : (Array.isArray(r.data) ? r.data : [])))
      .catch(() => setAllProviders([]));
  }, []);

  const providerById = useMemo(() => {
    const m = {};
    for (const p of allProviders) m[p.id] = p;
    // Fold in any team members the providers endpoint didn't return
    // (e.g. org_admin scope hides assistants from /api/providers but
    // they appear on the student team).
    for (const t of team) {
      if (!m[t.provider_id]) {
        m[t.provider_id] = {
          id: t.provider_id,
          first_name: t.provider_first_name,
          last_name: t.provider_last_name,
          role: t.provider_role,
        };
      }
    }
    return m;
  }, [allProviders, team]);

  const eligibleAdd = useMemo(() => {
    const taken = new Set(team.map((t) => t.provider_id));
    return allProviders.filter((p) => !taken.has(p.id));
  }, [allProviders, team]);

  const patch = async (delta, ownerRowId = "") => {
    if (ownerRowId) setSavingFor(ownerRowId);
    try {
      const r = await api.patchAdminStudent(studentId, delta);
      if (r.data?.ok) {
        toast.success("Team updated");
        onChanged?.();
      }
      return true;
    } catch (e) {
      // FP3-055 — surface backend validation cleanly
      const msg = e?.response?.data?.detail
        || e?.response?.data?.message
        || e.message
        || "Save failed";
      toast.error(msg);
      return false;
    } finally {
      if (ownerRowId) setSavingFor("");
    }
  };

  // Build a fully-formed team_assignments array with every existing
  // row's fields preserved + the caller's mutation applied to the
  // targeted row.
  const composeRows = (mutate) => team.map((t) => {
    const base = {
      provider_id: t.provider_id,
      primary_provider: t.primary_provider ?? false,
      provides_direct_service: t.provides_direct_service ?? true,
      evaluation_team_member: t.evaluation_team_member ?? false,
      assistant_assignment: t.assistant_assignment ?? false,
      supervising_provider_id: t.supervising_provider_id || "",
      assigned_service_domains: t.assigned_service_domains || [],
      assignment_status: t.assignment_status || "active",
      // FP3-093A — preserve the service-ended review flag across
      // every Team-tab PATCH so acked reviews stay acked.
      ended_review_ack: t.ended_review_ack ?? false,
    };
    return mutate ? mutate(base, t) : base;
  });

  // FP3-093A — admin acknowledges the "provider ended service" review.
  const markReviewed = (pid) => {
    const next = composeRows((row) =>
      row.provider_id === pid ? { ...row, ended_review_ack: true } : row
    );
    patch({ team_assignments: next }, pid);
  };

  const setPrimary = (pid) => {
    const next = composeRows((row) => ({ ...row, primary_provider: row.provider_id === pid }));
    patch({ team_assignments: next });
  };

  const removeProvider = (pid) => {
    const remaining = team.filter((t) => t.provider_id !== pid);
    const wasPrimary = team.find((t) => t.provider_id === pid)?.primary_provider;
    const next = remaining.map((t, idx) => ({
      provider_id: t.provider_id,
      primary_provider: wasPrimary ? (idx === 0) : !!t.primary_provider,
      provides_direct_service: t.provides_direct_service ?? true,
      evaluation_team_member: t.evaluation_team_member ?? false,
      assistant_assignment: t.assistant_assignment ?? false,
      supervising_provider_id: t.supervising_provider_id || "",
      assigned_service_domains: t.assigned_service_domains || [],
      assignment_status: t.assignment_status || "active",
    }));
    patch({
      assigned_providers: next.map((t) => t.provider_id),
      team_assignments: next,
    });
  };

  const addProvider = (pid) => {
    if (!pid) return;
    // FP3-093D §7 — never create the incomplete legacy shape: when the
    // provider is canonically an assistant, the row is flagged as an
    // assistant assignment up front. The backend safely infers the
    // student-level supervisor when exactly one authorized supervisor
    // is on the team, or rejects with a clear pick-a-supervisor error.
    const prov = allProviders.find((p) => p.id === pid) || {};
    const isAsst =
      isAssistantRole(prov.role) || prov.provider_role_type === "assistant";
    const next = composeRows();
    next.push({
      provider_id: pid,
      primary_provider: next.length === 0 && !isAsst,
      provides_direct_service: true,
      evaluation_team_member: false,
      assistant_assignment: isAsst,
      supervising_provider_id: "",
      assigned_service_domains: [],
      assignment_status: "active",
    });
    patch({
      assigned_providers: next.map((t) => t.provider_id),
      team_assignments: next,
    });
    setAdding(false);
  };

  const toggleFlag = (pid, flag) => {
    const next = composeRows((row) => {
      if (row.provider_id !== pid) return row;
      return { ...row, [flag]: !row[flag] };
    });
    patch({ team_assignments: next });
  };

  // FP3-093 — Per-relationship service status (Active / Monitoring /
  // Graduated / Archived). Changing it never touches the org-wide
  // student status or is_archived flag, and reactivating one provider
  // never auto-reactivates the global student record.
  // FP3-093C — a PRIMARY provider's status change cascades to the
  // assistants linked to them on THIS student (student-level
  // supervising_provider_id). Confirm with the impact first.
  const setAssignmentStatus = (pid, status) => {
    const row = team.find((t) => t.provider_id === pid);
    const isCurrentSt = (st) =>
      !["graduated", "archived"].includes((st || "active").toLowerCase());
    const ending = ["graduated", "archived"].includes(status);
    // FP3-093D — canonical classification (legacy rows may carry
    // assistant_assignment=false even though the provider IS an
    // assistant; the backend repairs + cascades them).
    const rowIsAssistant = (t) =>
      !!t.assistant_assignment ||
      t.provider_role_type === "assistant" ||
      !!t.provider_assistant_type;
    const currentPrimaryIds = team
      .filter((t) => !rowIsAssistant(t) && isCurrentSt(t.assignment_status))
      .map((t) => t.provider_id);
    const linkedTo = (t) => {
      const sup = (t.supervising_provider_id || "").trim();
      if (sup) return sup === pid;
      return currentPrimaryIds.length === 1 && currentPrimaryIds[0] === pid;
    };
    if (row && !rowIsAssistant(row)) {
      const linked = team.filter(
        (t) => rowIsAssistant(t) && linkedTo(t) && t.provider_id !== pid
      );
      const affected = ending
        ? linked.filter((t) => isCurrentSt(t.assignment_status))
        : linked.filter(
            (t) =>
              !isCurrentSt(t.assignment_status) &&
              t.status_cascade_source_provider_id === pid
          );
      if (affected.length > 0) {
        const names = affected
          .map((t) =>
            `${t.provider_first_name || ""} ${t.provider_last_name || ""}`.trim()
          )
          .filter(Boolean)
          .join(", ");
        const msg = ending
          ? `This will also mark the linked assistant assignment${affected.length === 1 ? "" : "s"} as ${status}: ${names}.`
          : `Linked assistant assignment${affected.length === 1 ? "" : "s"} ended by this provider's cascade will also be reactivated: ${names}.`;
        if (!window.confirm(msg)) return;
      }
    }
    const next = composeRows((r) => {
      if (r.provider_id !== pid) return r;
      return { ...r, assignment_status: status };
    });
    patch({ team_assignments: next }, pid);
  };

  // FP3-055 — Persist an assistant_assignment + supervising_provider_id
  // pair atomically. Called from the inline supervisor picker.
  const setAssistantWithSupervisor = async (pid, supervisorPid) => {
    if (!supervisorPid) {
      toast.error("Select a supervising provider before saving");
      return;
    }
    const next = composeRows((row) => {
      if (row.provider_id !== pid) return row;
      return {
        ...row,
        assistant_assignment: true,
        supervising_provider_id: supervisorPid,
      };
    });
    const ok = await patch({ team_assignments: next }, pid);
    if (ok) setSupervisorPickerFor("");
  };

  // FP3-055 — Clear assistant flag + supervisor in one shot.
  const unmarkAssistant = (pid) => {
    const next = composeRows((row) => {
      if (row.provider_id !== pid) return row;
      return { ...row, assistant_assignment: false, supervising_provider_id: "" };
    });
    patch({ team_assignments: next });
  };

  // Build the supervisor candidate list for a given assistant row.
  // Rules:
  //   - exclude the assistant themself
  //   - exclude every other assistant-role provider in the team
  //     and in the full provider list (no assistant → assistant
  //     supervision)
  //   - sort: compatible discipline first, then others
  const supervisorCandidates = (assistantRow) => {
    const assistantDisc = disciplineTag(assistantRow.provider_role
      || providerById[assistantRow.provider_id]?.role);
    // FP3-090 — when the assistant carries an org-level authorized
    // supervisor list, the student-level pick MUST come from it.
    const authorized = (assistantRow.authorized_supervisor_ids || []).filter(Boolean);
    const candidates = allProviders.filter((p) => {
      if (p.id === assistantRow.provider_id) return false;
      if (isAssistantRole(p.role)) return false;
      if (authorized.length > 0 && !authorized.includes(p.id)) return false;
      return true;
    });
    candidates.sort((a, b) => {
      const ac = disciplineTag(a.role) === assistantDisc ? 0 : 1;
      const bc = disciplineTag(b.role) === assistantDisc ? 0 : 1;
      if (ac !== bc) return ac - bc;
      const an = `${a.first_name || ""} ${a.last_name || ""}`.trim().toLowerCase();
      const bn = `${b.first_name || ""} ${b.last_name || ""}`.trim().toLowerCase();
      return an.localeCompare(bn);
    });
    return { candidates, assistantDisc, authorized };
  };

  const rows = compactPreview ? team.slice(0, 3) : team;

  return (
    <Card className="p-5 bg-white border-[#E5E3DB]" data-testid="admin-team-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[13px] font-medium text-[#1F2937]">
          Provider team · {team.length}
        </p>
        {!readOnly && !adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            data-testid="admin-team-add"
            className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-[#1F2937] text-white hover:bg-black inline-flex items-center gap-1"
          >
            <UserPlus size={12} /> Add provider
          </button>
        )}
        {!readOnly && adding && (
          <div className="flex items-center gap-2">
            <select
              data-testid="admin-team-add-picker"
              defaultValue=""
              onChange={(e) => addProvider(e.target.value)}
              className="h-8 text-[12px] border border-[#d4d2cc] rounded-md px-2 bg-white"
            >
              <option value="" disabled>Pick a provider…</option>
              {eligibleAdd.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.first_name || ""} {p.last_name || ""} {p.role ? `· ${p.role}` : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="text-[11px] text-[#6B7280] underline"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {team.length === 0 && (
        <p
          className="text-[12px] text-[#6B7280] italic"
          data-testid="admin-team-empty"
        >
          No providers assigned yet. {!readOnly && "Use Add provider to start a team."}
        </p>
      )}

      <div className="space-y-2">
        {rows.map((t) => {
          const supervisorName = (() => {
            const p = providerById[t.supervising_provider_id];
            if (!p) return "";
            return `${p.first_name || ""} ${p.last_name || ""}`.trim();
          })();
          const supervisorRole = providerById[t.supervising_provider_id]?.role || "";
          const pickerOpen = supervisorPickerFor === t.provider_id;
          const { candidates, assistantDisc, authorized } = supervisorCandidates(t);
          return (
            <div
              key={t.provider_id}
              data-testid={`admin-team-row-${t.provider_id}`}
              className="border border-[#E5E3DB] rounded-lg p-3 flex items-start gap-3 flex-wrap"
            >
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[13px] font-medium text-[#1F2937]">
                    {(t.provider_first_name || "") + " " + (t.provider_last_name || "")}
                  </p>
                  {t.provider_role && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#F3F1EC] text-[#4B5563]">
                      {t.provider_role}
                    </span>
                  )}
                  {t.primary_provider && (
                    <span
                      data-testid={`admin-team-primary-chip-${t.provider_id}`}
                      className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#FBEBC0] text-[#7A6A2C] inline-flex items-center gap-1"
                    >
                      <Crown size={10} /> Primary
                    </span>
                  )}
                  {t.provides_direct_service && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#E8F2EE] text-[#2F6B53]">
                      Direct
                    </span>
                  )}
                  {t.evaluation_team_member && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#EEE9F7] text-[#5A4B85]">
                      Eval team
                    </span>
                  )}
                  {t.assistant_assignment && (
                    <span
                      data-testid={`admin-team-assistant-chip-${t.provider_id}`}
                      className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#F3F4F6] text-[#4B5563]"
                    >
                      Assistant
                    </span>
                  )}
                  {/* FP3-093 / FP3-093A — service-relationship status
                      chip. Ended relationships get STRONG styling so
                      Graduated/Archived history is unmissable. */}
                  {(() => {
                    const st = (t.assignment_status || "active").toLowerCase();
                    const chip = {
                      active: "bg-[#E1EAE4] text-[#1E5A3F] border border-[#C4D8CB]",
                      monitoring: "bg-[#E8F0FA] text-[#1D4E89] border border-[#B9D2EC]",
                      graduated: "bg-[#FDF0DC] text-[#8A4B08] border border-[#E8B04B] font-bold",
                      archived: "bg-[#F2E7E5] text-[#7A3B3B] border border-[#CFA9A4] font-bold",
                    }[st] || "bg-[#F3F1EC] text-[#6B7280] border border-[#DDD9D0]";
                    return (
                      <span
                        data-testid={`admin-team-status-chip-${t.provider_id}`}
                        className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${chip}`}
                      >
                        {st}
                      </span>
                    );
                  })()}
                </div>
                {/* FP3-055 — "Supervised by …" indicator on assistant rows */}
                {t.assistant_assignment && supervisorName && (
                  <p
                    className="text-[11px] text-[#3F4756] mt-1 inline-flex items-center gap-1"
                    data-testid={`admin-team-supervised-by-${t.provider_id}`}
                  >
                    <ShieldCheck size={11} className="text-[#1E3F33]" />
                    <span>
                      Supervised by <span className="font-medium">{supervisorName}</span>
                      {supervisorRole ? ` · ${supervisorRole}` : ""}
                    </span>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => setSupervisorPickerFor(t.provider_id)}
                        data-testid={`admin-team-change-supervisor-${t.provider_id}`}
                        className="ml-1 text-[#6B7280] hover:text-[#1E3F33] inline-flex items-center"
                        title="Change supervising provider"
                      >
                        <PencilSimple size={11} />
                      </button>
                    )}
                  </p>
                )}
                {(t.assigned_service_domains || []).length > 0 && (
                  <p className="text-[11px] text-[#6B7280] mt-1">
                    Domains: {(t.assigned_service_domains || []).join(", ")}
                  </p>
                )}

                {/* FP3-055 — Inline supervisor picker */}
                {!readOnly && pickerOpen && (
                  <div
                    className="mt-2 p-2 border border-[#E5E3DB] bg-[#FBFAF6] rounded-md"
                    data-testid={`admin-team-supervisor-picker-${t.provider_id}`}
                  >
                    <p className="text-[11px] text-[#3F4756] mb-1.5">
                      {authorized.length > 0 ? (
                        <>
                          Pick the supervising provider for this student —
                          limited to this assistant&apos;s{" "}
                          {authorized.length} authorized supervisor{authorized.length === 1 ? "" : "s"}.
                        </>
                      ) : (
                        <>
                          Pick the supervising provider (same organization, primary providers only).
                          {assistantDisc !== "other" && (
                            <span className="text-[#6B7280]">
                              {" "}Compatible disciplines for{" "}
                              {assistantDisc === "speech" ? "SLPA" : assistantDisc === "ot" ? "COTA" : assistantDisc === "pt" ? "PTA" : assistantDisc} are listed first.
                            </span>
                          )}
                        </>
                      )}
                    </p>
                    <div className="flex items-center gap-2">
                      <select
                        data-testid={`admin-team-supervisor-select-${t.provider_id}`}
                        defaultValue={t.supervising_provider_id || ""}
                        onChange={(e) => setAssistantWithSupervisor(t.provider_id, e.target.value)}
                        disabled={savingFor === t.provider_id}
                        className="h-8 text-[12px] border border-[#d4d2cc] rounded-md px-2 bg-white flex-1 min-w-[200px]"
                      >
                        <option value="" disabled>Pick a supervisor…</option>
                        {candidates.map((p) => (
                          <option key={p.id} value={p.id}>
                            {(p.first_name || "")} {(p.last_name || "")} {p.role ? `· ${p.role}` : ""}
                            {disciplineTag(p.role) === assistantDisc ? "  ★" : ""}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setSupervisorPickerFor("")}
                        className="text-[11px] text-[#6B7280] hover:text-[#1F2937] underline"
                        data-testid={`admin-team-supervisor-cancel-${t.provider_id}`}
                      >
                        Cancel
                      </button>
                    </div>
                    {candidates.length === 0 && (
                      <p
                        className="text-[11px] text-[#A8453A] mt-1"
                        data-testid={`admin-team-supervisor-empty-${t.provider_id}`}
                      >
                        No eligible supervising providers in this organization yet.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1.5 flex-wrap">
                <Link
                  to={`/students/${studentId}?as_provider=${t.provider_id}&from_admin_overview=1`}
                  data-testid={`admin-team-view-${t.provider_id}`}
                  className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-[#E5E3DB] hover:bg-[#F3F1EC] inline-flex items-center gap-1"
                >
                  <Eye size={11} /> View as
                </Link>
                {!readOnly && !t.primary_provider && (
                  <button
                    type="button"
                    onClick={() => setPrimary(t.provider_id)}
                    data-testid={`admin-team-set-primary-${t.provider_id}`}
                    className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-[#E5E3DB] hover:bg-[#F3F1EC]"
                  >
                    Make primary
                  </button>
                )}
                {!readOnly && (
                  <>
                    {/* FP3-093 — Reactivate an ended relationship, or
                        set any per-provider service status. */}
                    {["graduated", "archived"].includes(
                      (t.assignment_status || "active").toLowerCase()
                    ) && (
                      <button
                        type="button"
                        onClick={() => setAssignmentStatus(t.provider_id, "active")}
                        disabled={savingFor === t.provider_id}
                        data-testid={`admin-team-reactivate-${t.provider_id}`}
                        className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-[#CBDDD2] text-[#1E5A3F] hover:bg-[#EAF3EE]"
                        title="Reactivate this provider's service relationship (does not change the student's organization-wide status)"
                      >
                        Reactivate service
                      </button>
                    )}
                    {/* FP3-093A — the review alert (and its admin
                        notification) stays until acknowledged. */}
                    {["graduated", "archived"].includes(
                      (t.assignment_status || "active").toLowerCase()
                    ) && !t.ended_review_ack && (
                      <button
                        type="button"
                        onClick={() => markReviewed(t.provider_id)}
                        disabled={savingFor === t.provider_id}
                        data-testid={`admin-team-mark-reviewed-${t.provider_id}`}
                        className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-[#E8B04B] text-[#8A4B08] hover:bg-[#FDF0DC]"
                        title="Mark this service-ended review as complete (keeps the relationship visible as history)"
                      >
                        Mark reviewed
                      </button>
                    )}
                    <select
                      data-testid={`admin-team-status-select-${t.provider_id}`}
                      value={(t.assignment_status || "active").toLowerCase()}
                      onChange={(e) => setAssignmentStatus(t.provider_id, e.target.value)}
                      disabled={savingFor === t.provider_id}
                      title="Service relationship status for this provider only"
                      className="h-7 text-[11px] border border-[#d4d2cc] rounded-md px-1.5 bg-white"
                    >
                      <option value="active">Active</option>
                      <option value="monitoring">Monitoring</option>
                      <option value="graduated">Graduated</option>
                      <option value="archived">Archived</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => toggleFlag(t.provider_id, "provides_direct_service")}
                      data-testid={`admin-team-toggle-direct-${t.provider_id}`}
                      className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-[#E5E3DB] hover:bg-[#F3F1EC]"
                      title="Toggle direct service"
                    >
                      {t.provides_direct_service ? "Set consult" : "Set direct"}
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleFlag(t.provider_id, "evaluation_team_member")}
                      data-testid={`admin-team-toggle-eval-${t.provider_id}`}
                      className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-[#E5E3DB] hover:bg-[#F3F1EC]"
                    >
                      {t.evaluation_team_member ? "Unmark eval team" : "Eval team"}
                    </button>
                    {/* FP3-055 — Mark / unmark assistant with supervisor picker.
                        A primary-provider row cannot also be an assistant
                        on the same student, so the action is hidden there. */}
                    {!t.assistant_assignment && !t.primary_provider && (
                      <button
                        type="button"
                        onClick={() => {
                          // FP3-090 — exactly one authorized supervisor
                          // → auto-select it; more than one → the admin
                          // must choose explicitly (never default to
                          // the first ID).
                          if (candidates.length === 1) {
                            setAssistantWithSupervisor(t.provider_id, candidates[0].id);
                          } else {
                            setSupervisorPickerFor(t.provider_id);
                          }
                        }}
                        data-testid={`admin-team-mark-assistant-${t.provider_id}`}
                        className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-[#E5E3DB] hover:bg-[#F3F1EC] inline-flex items-center gap-1"
                        title="Mark this row as an assistant assignment and pick the supervising provider"
                      >
                        <ShieldCheck size={11} /> Mark assistant
                      </button>
                    )}
                    {t.assistant_assignment && (
                      <button
                        type="button"
                        onClick={() => unmarkAssistant(t.provider_id)}
                        data-testid={`admin-team-unmark-assistant-${t.provider_id}`}
                        className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-[#E5E3DB] hover:bg-[#F3F1EC] inline-flex items-center gap-1"
                        title="Remove the assistant flag and supervising provider link"
                      >
                        <XIcon size={11} /> Unmark assistant
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        const st = (t.assignment_status || "active").toLowerCase();
                        const endedNote = ["graduated", "archived"].includes(st)
                          ? ` This row currently records a ${st.toUpperCase()} service relationship — removing it erases that history chip from the Team tab (goals and sessions stay attributed).`
                          : " Historical sessions and goals remain attributed.";
                        if (window.confirm(`Remove this provider from the student's team?${endedNote}`)) {
                          removeProvider(t.provider_id);
                        }
                      }}
                      data-testid={`admin-team-remove-${t.provider_id}`}
                      className="px-2.5 py-1 rounded-md text-[11px] font-medium text-[#A8453A] hover:bg-[#FBEEEE] border border-[#F4D1D1] inline-flex items-center gap-1"
                    >
                      <UserMinus size={11} /> Remove
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {compactPreview && team.length > rows.length && (
        <p className="mt-3 text-[11px] text-[#6B7280]">
          Showing {rows.length} of {team.length}. Open the Team tab for the full roster.
        </p>
      )}
    </Card>
  );
}
